import {
  DESCANSO_PADRAO,
  ROTULO_DA_MATERIA,
  abaixoDaMateria,
  materiaDe,
  type CategoriaResumo,
} from "@noesis/shared";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { emEnsaio, FalhaDaApi, pedir } from "../api.js";
import { Icone } from "../componentes/Icone.js";
import { CirculoMao } from "../componentes/Tracos.js";
import { TRILHA_DO_ENSAIO } from "../componentes/tour/ensaio.js";
import { IconeCriar, IconeSalvar, IconeVoltar } from "../componentes/icones.js";
import { useSessao } from "../estado/sessao.js";
import { useTour } from "../estado/tour.js";
import { useTrilhas } from "../estado/trilhas.js";

/** Rótulos, não regra: a ordem é a do índice do dia da semana, 0 domingo. */
const DIAS_DA_SEMANA = [
  { curto: "dom", longo: "domingo" },
  { curto: "seg", longo: "segunda-feira" },
  { curto: "ter", longo: "terça-feira" },
  { curto: "qua", longo: "quarta-feira" },
  { curto: "qui", longo: "quinta-feira" },
  { curto: "sex", longo: "sexta-feira" },
  { curto: "sáb", longo: "sábado" },
];

/**
 * A dosagem tem estado próprio (invariante 3): ela vem de duas rotas que não
 * são as da trilha, e falha sozinha. Dobrá-la no estado do formulário faria
 * uma lista de matérias que não carregou parecer trilha que não carregou.
 *
 * `vazio` é trilha sem matéria nenhuma — não é erro, é a trilha que ainda não
 * recebeu material.
 */
type EstadoDosagem =
  | { nome: "carregando" }
  | { nome: "vazio" }
  | { nome: "erro"; mensagem: string }
  | { nome: "pronto"; materias: CategoriaResumo[] };

/**
 * Criar e editar trilha — a mesma tela para os dois, porque os campos são os
 * mesmos e duas telas quase iguais divergem com o tempo.
 *
 * Os limites da meta vêm de `sessao.limites`, que o servidor calculou a partir
 * da configuração. Nenhum número de regra é digitado aqui (invariante 4).
 */
export function TrilhaFormulario() {
  const { id } = useParams();
  const editando = Boolean(id);
  const navegar = useNavigate();
  const { limites } = useSessao();
  const { estado, aplicar } = useTrilhas();
  const tour = useTour();

  // No ensaio do tour o nome já vem escrito: o passo é só criar.
  const [nome, setNome] = useState(() => (!editando && emEnsaio() ? TRILHA_DO_ENSAIO : ""));
  const [iniciais, setIniciais] = useState("");
  const [descricao, setDescricao] = useState("");
  const [meta, setMeta] = useState(String(limites.itensPorSessao));
  const [dataAlvo, setDataAlvo] = useState("");

  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [descanso, setDescanso] = useState<number[]>([...DESCANSO_PADRAO]);
  const [alfa, setAlfa] = useState("1");
  const [beta, setBeta] = useState("3");
  const [campos, setCampos] = useState<Record<string, string>>({});
  /**
   * Opções avançadas recolhidas na criação (ADR 041): só o nome é
   * obrigatório, meta e descanso já vêm prontos, e sigla, data-alvo,
   * descrição e a ordem da fila esperam quem precisar delas. Na edição abrem,
   * para nada do que já foi escolhido ficar escondido.
   */
  const [avancadas, setAvancadas] = useState(editando);
  // Sigla vazia usa as iniciais do nome (9b): "Banco de Dados" vira "BDD".
  const siglaEfetiva = (iniciais.trim() || siglaDoNome(nome)).toLocaleUpperCase("pt-BR").slice(0, 4);

  const [dosagem, setDosagem] = useState<EstadoDosagem>({ nome: "carregando" });
  const [alvos, setAlvos] = useState<Record<string, string>>({});

  /**
   * `dosagem.ler`, `dosagem.definir` e `categoria.listar` são as três da trilha
   * **ativa** — nenhuma recebe id de trilha. Editar outra trilha aqui não dá
   * acesso à dosagem dela, e ler a da ativa mostrando o nome desta seria pior
   * do que não mostrar nada.
   */
  const ehAtiva = editando && estado.nome === "pronto" && estado.ativaId === id;

  // Ao editar, os valores vêm da lista que já está em memória: a rota que abriu
  // esta tela já carregou tudo. Buscar de novo só para preencher o formulário
  // seria uma ida ao servidor para descobrir o que a tela anterior já sabia.
  useEffect(() => {
    if (!editando || estado.nome !== "pronto") return;
    const t = estado.trilhas.find((x) => x.id === id);
    if (!t) return;
    setNome(t.nome);
    setIniciais(t.iniciais);
    setDescricao(t.descricao ?? "");
    setMeta(String(t.metaDiaria));
    setDataAlvo(t.dataAlvo ?? "");
    setDescanso(t.diasDeDescanso);
    setAlfa(String(t.reincidenciaAlfa));
    setBeta(String(t.reincidenciaBeta));
  }, [editando, id, estado]);

  /**
   * As matérias e os alvos já gravados, numa ida só.
   *
   * A **matéria é a raiz da árvore** (RN-37, RN-38), e o filtro é sobre o
   * caminho — nada abaixo dela —, não sobre `profundidade`: é o mesmo critério
   * que `materia.ts` usa em todo o resto, e o caminho já viaja no contrato.
   *
   * As rotas aceitam categoria de qualquer nível, então a leitura pode trazer
   * alvo de um assunto. Ele não some do banco; só não aparece aqui, porque
   * esta tela edita matéria.
   */
  const carregarDosagem = useCallback(async () => {
    setDosagem({ nome: "carregando" });
    try {
      const [c, d] = await Promise.all([pedir("categoria.listar"), pedir("dosagem.ler")]);
      const materias = c.categorias.filter((x) => abaixoDaMateria(x.caminho).length === 0);
      const gravados = new Map(d.itens.map((i) => [i.categoriaId, i.alvoDiario]));
      setAlvos(Object.fromEntries(materias.map((m) => [m.id, String(gravados.get(m.id) ?? 0)])));
      setDosagem(materias.length === 0 ? { nome: "vazio" } : { nome: "pronto", materias });
    } catch (falha) {
      setDosagem({
        nome: "erro",
        mensagem: falha instanceof FalhaDaApi ? falha.message : "Não foi possível carregar as matérias desta trilha.",
      });
    }
  }, []);

  useEffect(() => {
    if (!ehAtiva) return;
    void carregarDosagem();
  }, [ehAtiva, carregarDosagem]);

  /**
   * Alvo zero é **ausência de cota**, e o contrato só aceita inteiro positivo:
   * a matéria zerada sai da lista em vez de viajar como zero. Todas zeradas
   * mandam lista vazia, que é como a RN-19 desliga a dosagem da trilha.
   */
  function itensDaDosagem() {
    if (dosagem.nome !== "pronto") return [];
    return dosagem.materias
      .map((m) => ({ categoriaId: m.id, alvoDiario: Math.trunc(Number(alvos[m.id] ?? "0")) }))
      .filter((i) => Number.isFinite(i.alvoDiario) && i.alvoDiario > 0);
  }

  const naoEncontrada = editando && estado.nome === "pronto" && !estado.trilhas.some((t) => t.id === id);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    if (enviando) return;
    setEnviando(true);
    setErro(null);
    setCampos({});

    const corpo = {
      nome,
      iniciais: siglaEfetiva,
      descricao: descricao.trim() === "" ? null : descricao,
      metaDiaria: Number(meta),
      dataAlvo: dataAlvo === "" ? null : dataAlvo,
      diasDeDescanso: descanso,
      reincidenciaAlfa: Number(alfa),
      reincidenciaBeta: Number(beta),
    };

    try {
      const r =
        editando && id
          ? await pedir("trilha.editar", { params: { id }, corpo })
          : await pedir("trilha.criar", { corpo });
      aplicar(r);
      if (!editando) tour.avancarSe("criarBtn");

      // A dosagem é uma segunda gravação, em rota própria. Vem depois porque
      // salvar a trilha é o que o botão promete: se esta falhar, o que já foi
      // gravado continua gravado, e a mensagem precisa dizer isso — em vez de
      // sugerir que nada foi salvo e convidar a salvar tudo de novo.
      if (ehAtiva && dosagem.nome === "pronto") {
        try {
          await pedir("dosagem.definir", { corpo: { itens: itensDaDosagem() } });
        } catch (falha) {
          setErro(
            falha instanceof FalhaDaApi
              ? `A trilha foi salva, mas a dosagem não: ${falha.message}`
              : "A trilha foi salva, mas não foi possível salvar a dosagem.",
          );
          setEnviando(false);
          return;
        }
      }

      void navegar("/", { replace: true });
    } catch (falha) {
      if (falha instanceof FalhaDaApi) {
        setErro(falha.message);
        const porCampo = Object.fromEntries((falha.campos ?? []).map((c) => [c.caminho, c.mensagem]));
        setCampos(porCampo);
        // Erro num campo recolhido abre o bloco: mensagem escondida não existe.
        if (["iniciais", "dataAlvo", "reincidenciaAlfa", "reincidenciaBeta"].some((c) => c in porCampo)) {
          setAvancadas(true);
        }
      } else {
        setErro("Não foi possível salvar a trilha.");
      }
      setEnviando(false);
    }
  }

  if (naoEncontrada) {
    return (
      <div className="tela">
        <div className="tela__corpo">
          <section className="estado">
            <h1 className="estado__titulo">Essa trilha não existe mais</h1>
            <p className="estado__texto">Ela pode ter sido excluída em outro aparelho.</p>
            <Link className="botao" to="/trilhas">
              Ver minhas trilhas
            </Link>
          </section>
        </div>
      </div>
    );
  }

  const descansoTexto = (() => {
    if (descanso.length === 0) return null;
    const dias = descanso.map((d) => DIAS_DA_SEMANA[d]!.curto);
    return `descanso ${dias.length === 1 ? dias[0] : `${dias.slice(0, -1).join(", ")} e ${dias.at(-1)}`}`;
  })();
  const semHistorico = Number(alfa) / (Number(alfa) + Number(beta));

  return (
    <div className="trilha-form">
      <header className="trilha-form__cabecalho">
        <Link className="link-mao link-mao--tinta trilha-form__voltar" to={editando ? "/acervo?aba=trilhas" : "/"}>
          <Icone glifo={IconeVoltar} papel="acao" tamanho="p" />
          {editando ? "voltar ao acervo" : "voltar ao início"}
        </Link>
        <h1 className="t-titulo">{editando ? "Editar trilha" : "Nova trilha"}</h1>
        {!editando && (
          <p className="t-corpo t-2 trilha-form__descricao">
            Pode ser larga como “Direito Constitucional” ou estreita como “Controle difuso”.
          </p>
        )}
      </header>

      <aside className="postit trilha-form__previa" aria-label="Prévia da trilha">
        <p className="t-anotacao-p">assim ela vai aparecer</p>
        <div className="trilha-form__previa-linha">
          <span className="carimbo" aria-hidden="true">
            {siglaEfetiva || "SIG"}
          </span>
          <div>
            <p className="postit__titulo">{nome.trim() || "Nome da trilha"}</p>
            <p className="postit__texto">{[`${meta || 0} por dia`, descansoTexto].filter(Boolean).join(" · ")}</p>
          </div>
        </div>
      </aside>

      <form className="trilha-form__campos" id="formulario-trilha" onSubmit={(e) => void enviar(e)} noValidate>
        <div className="trilha-form__coluna">
          <div className="campo">
            <label className="campo__rotulo" htmlFor="nome">
              Nome
            </label>
            <input
              id="nome"
              className="campo__entrada"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              maxLength={80}
              required
              placeholder="ex.: Direito Constitucional"
              autoFocus={!editando}
              aria-describedby={campos.nome ? "erro-nome" : undefined}
              aria-invalid={campos.nome ? true : undefined}
            />
            {campos.nome && (
              <p className="campo__erro" id="erro-nome">
                {campos.nome}
              </p>
            )}
          </div>

          <div className="trilha-form__ritmo">
            <div className="campo">
              <label className="campo__rotulo" htmlFor="meta">
                Meta diária
              </label>
              <span className="campo-com-sufixo">
                <input
                  id="meta"
                  className="campo__entrada campo__entrada--numero"
                  type="number"
                  inputMode="numeric"
                  min={limites.metaMinima}
                  max={limites.metaMaxima}
                  value={meta}
                  onChange={(e) => setMeta(e.target.value)}
                  aria-describedby="ajuda-meta"
                  aria-invalid={campos.metaDiaria ? true : undefined}
                />
                <span className="t-anotacao t-2">questões por dia</span>
              </span>
              {campos.metaDiaria && (
                <p className="campo__erro" id="ajuda-meta">
                  {campos.metaDiaria}
                </p>
              )}
            </div>

            <fieldset className="campo campo--grupo">
              <legend className="campo__rotulo">Dias de descanso</legend>
              <div className="dias-mao" role="group" aria-describedby="ajuda-descanso">
                {DIAS_DA_SEMANA.map((dia, i) => {
                  const marcado = descanso.includes(i);
                  return (
                    <button
                      key={dia.curto}
                      type="button"
                      className={marcado ? "dia-mao dia-mao--marcado" : "dia-mao"}
                      aria-pressed={marcado}
                      aria-label={dia.longo}
                      onClick={() =>
                        setDescanso(marcado ? descanso.filter((d) => d !== i) : [...descanso, i].sort((a, b) => a - b))
                      }
                    >
                      {dia.curto}
                      <CirculoMao feito={marcado} />
                    </button>
                  );
                })}
              </div>
            </fieldset>
          </div>
          <p className="campo__ajuda trilha-form__ajuda-ritmo" id="ajuda-descanso">
            {campos.diasDeDescanso ?? "Descanso não cobra a meta nem quebra a sequência."}
          </p>

          <button
            type="button"
            className="trilha-form__avancadas-botao"
            aria-expanded={avancadas}
            aria-controls="opcoes-avancadas"
            onClick={() => setAvancadas((v) => !v)}
          >
            <Icone glifo={avancadas ? "chevron_down" : "chevron_right"} papel="acao" tamanho="p" />
            <span className="t-anotacao t-2">opções avançadas</span>
            {!avancadas && (
              <span className="t-ajuda trilha-form__avancadas-dica">data-alvo, descrição, ordem da fila de erradas</span>
            )}
          </button>

          {avancadas && (
            <div className="trilha-form__avancadas" id="opcoes-avancadas">
              <div className="campo">
                <label className="campo__rotulo" htmlFor="data-alvo">
                  Data-alvo
                </label>
                <input
                  id="data-alvo"
                  className="campo__entrada campo__entrada--data"
                  type="date"
                  value={dataAlvo}
                  onChange={(e) => setDataAlvo(e.target.value)}
                  aria-describedby="ajuda-data"
                />
                <p className="campo__ajuda" id="ajuda-data">
                  {campos.dataAlvo ?? "A prova, a entrega, o fim do semestre. Vira contagem regressiva."}
                </p>
              </div>

              <div className="campo">
                <label className="campo__rotulo" htmlFor="iniciais">
                  Sigla
                </label>
                <input
                  id="iniciais"
                  className="campo__entrada campo__entrada--curto"
                  value={iniciais}
                  onChange={(e) => setIniciais(e.target.value.toLocaleUpperCase("pt-BR"))}
                  maxLength={4}
                  placeholder={siglaDoNome(nome).toLocaleUpperCase("pt-BR") || "ex.: DC"}
                  autoCapitalize="characters"
                  autoComplete="off"
                  spellCheck={false}
                  aria-describedby="ajuda-iniciais"
                  aria-invalid={campos.iniciais ? true : undefined}
                />
                <p className="campo__ajuda" id="ajuda-iniciais">
                  {campos.iniciais ?? "Até quatro letras. Vazia, usa as iniciais do nome."}
                </p>
              </div>

              <div className="campo trilha-form__largo">
                <label className="campo__rotulo" htmlFor="descricao">
                  Descrição
                </label>
                <textarea
                  id="descricao"
                  className="campo__area"
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  maxLength={500}
                  rows={2}
                  placeholder="o que entra nesta trilha"
                />
              </div>

              <fieldset className="campo campo--grupo trilha-form__largo">
                <legend className="campo__rotulo">Ordem da fila de erradas</legend>
                <span className="campo-com-sufixo">
                  <label className="t-anotacao t-2" htmlFor="alfa">
                    alfa
                  </label>
                  <input
                    id="alfa"
                    className="campo__entrada campo__entrada--numero"
                    type="number"
                    inputMode="decimal"
                    step="0.5"
                    min="0.5"
                    value={alfa}
                    onChange={(e) => setAlfa(e.target.value)}
                    required
                    aria-invalid={campos.reincidenciaAlfa ? true : undefined}
                  />
                  <label className="t-anotacao t-2" htmlFor="beta">
                    beta
                  </label>
                  <input
                    id="beta"
                    className="campo__entrada campo__entrada--numero"
                    type="number"
                    inputMode="decimal"
                    step="0.5"
                    min="0.5"
                    value={beta}
                    onChange={(e) => setBeta(e.target.value)}
                    required
                    aria-invalid={campos.reincidenciaBeta ? true : undefined}
                  />
                </span>
                <p className="campo__ajuda">
                  {campos.reincidenciaAlfa ??
                    campos.reincidenciaBeta ??
                    `A fila ordena por (erros + alfa) ÷ (tentativas + alfa + beta). Item sem histórico vale ${
                      Number.isFinite(semHistorico) ? semHistorico.toFixed(2).replace(".", ",") : "–"
                    }. Alfa maior sobe quem tem pouca amostra; beta maior pede mais prova antes de subir.`}
                </p>
              </fieldset>
            </div>
          )}
        </div>

        {editando && (
          <div className="trilha-form__dosagem">
            {!ehAtiva && (
              <p className="campo__ajuda">
                A dosagem por {ROTULO_DA_MATERIA.toLowerCase()} é da trilha em uso. Para repartir a meta desta trilha
                entre as matérias, troque para ela pelo seletor de trilha e volte a este formulário.
              </p>
            )}

            {ehAtiva && (
              <fieldset className="campo campo--grupo">
                <legend className="campo__rotulo">Alvo diário por {ROTULO_DA_MATERIA.toLowerCase()}</legend>

                {dosagem.nome === "carregando" && (
                  <p className="campo__ajuda" aria-live="polite">
                    Carregando as matérias desta trilha…
                  </p>
                )}

                {dosagem.nome === "erro" && (
                  <>
                    <p className="campo__erro" role="alert">
                      {dosagem.mensagem}
                    </p>
                    <button className="botao botao--secundario" type="button" onClick={() => void carregarDosagem()}>
                      Tentar de novo
                    </button>
                  </>
                )}

                {dosagem.nome === "vazio" && (
                  <div className="estado">
                    <p className="estado__titulo">Nenhuma matéria nesta trilha</p>
                    <p className="estado__texto">
                      A matéria é a raiz da árvore de categorias, e ela chega junto com o material. Importe um lote e as
                      matérias aparecem aqui para dosar.
                    </p>
                    <Link className="botao" to="/acervo?aba=importar">
                      Importar itens
                    </Link>
                  </div>
                )}

                {dosagem.nome === "pronto" && (
                  <>
                    <ul className="pilha">
                      {dosagem.materias.map((m) => (
                        <li className="linha linha--espalhada dosagem__linha" key={m.id}>
                          <label className="campo__rotulo" htmlFor={`alvo-${m.id}`}>
                            {materiaDe(m.caminho) ?? m.nome}
                          </label>
                          <input
                            id={`alvo-${m.id}`}
                            className="campo__entrada campo__entrada--curto"
                            type="number"
                            inputMode="numeric"
                            min={0}
                            step={1}
                            value={alvos[m.id] ?? "0"}
                            onChange={(e) => setAlvos({ ...alvos, [m.id]: e.target.value })}
                          />
                        </li>
                      ))}
                    </ul>
                    <div className="linha linha--espalhada dosagem__linha">
                      <span className="etiqueta dado">
                        Soma dos alvos {itensDaDosagem().reduce((s, i) => s + i.alvoDiario, 0)}
                      </span>
                      <span className="etiqueta dado">Meta diária {meta === "" ? 0 : Number(meta)}</span>
                    </div>
                    <p className="campo__ajuda">
                      A soma não precisa bater com a meta. Matéria em zero fica sem cota; todas em zero desligam a
                      dosagem desta trilha.
                    </p>
                  </>
                )}
              </fieldset>
            )}
          </div>
        )}

        <p className="campo__erro trilha-form__erro" role="alert" aria-live="polite">
          {erro ?? ""}
        </p>

        <div className="trilha-form__acoes">
          <button className="botao" type="submit" data-tour="criarBtn" disabled={enviando || !nome.trim()}>
            <Icone glifo={editando ? IconeSalvar : IconeCriar} papel="acao" />
            {enviando ? "Salvando…" : editando ? "Salvar trilha" : "Criar trilha"}
          </button>
          <Link className="link-mao link-mao--tinta" to={editando ? "/acervo?aba=trilhas" : "/"}>
            cancelar
          </Link>
        </div>
      </form>
    </div>
  );
}

/** As iniciais do nome, para a sigla que ficou em branco: "Banco de Dados" → "BDD". */
function siglaDoNome(nome: string): string {
  const palavras = nome.trim().split(/\s+/).filter(Boolean);
  if (palavras.length === 0) return "";
  if (palavras.length === 1) return palavras[0]!.slice(0, 3);
  return palavras.map((p) => p[0]).join("").slice(0, 4);
}
